import {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import noop from 'lodash/noop';

import { t } from '@lingui/macro';

import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';

import useSentinelTeamForm from 'components/SentinelTeamForm/hooks/useSentinelTeamForm';

import { SourceValidationError } from 'utils/SentinelTeam';
import { useCurrentUserContext } from 'utils/User';

import { TeamSource } from 'pages/Teams/CreateTeamModal/TeamSettingsStep/SourceItem/types';

import useCreateManagementTeamMutation from '../../hooks/useCreateManagementTeamMutation';

export type { TeamSource };

interface ManagementTeamModalValues {
  teamName: string;
  applicationName: string;
  teamKey: string;
  sourceIds: TeamSource[];
  username: string;
  password: string;
  ownerEmail: string;
  // Selected OEM segment for the team, when applicable.
  oemSegmentId: string | undefined;
}

interface ManagementTeamModalContextType {
  values: ManagementTeamModalValues;
  setValues: React.Dispatch<React.SetStateAction<ManagementTeamModalValues>>;
  isEditMode: false;
  sourceIdsWithErrors: TeamSource[];
  invalidEmailSources: TeamSource[];
  sourceIdError: SourceValidationError[];
  sourceIdsValid: boolean;
  setSourceIdError: React.Dispatch<
    React.SetStateAction<SourceValidationError[]>
  >;
  canGoNext: (page: number) => boolean;
  canSubmit: boolean;
  createTeam: () => Promise<void>;
  createLoading: boolean;
  reset: () => void;
  logoUrl: string | undefined;
  onSaveImage: ((file: any) => void) | undefined;
  requiresOemSegmentSelector: boolean;
}

const defaultValues: ManagementTeamModalValues = {
  teamName: '',
  applicationName: '',
  teamKey: '',
  sourceIds: [],
  username: '',
  password: '',
  ownerEmail: '',
  oemSegmentId: undefined,
};

const INITIAL_CONTEXT: ManagementTeamModalContextType = {
  values: defaultValues,
  setValues: noop,
  isEditMode: false,
  sourceIdsWithErrors: [],
  invalidEmailSources: [],
  sourceIdError: [],
  sourceIdsValid: true,
  setSourceIdError: noop,
  canGoNext: /* istanbul ignore next -- @preserve */ (_n) => false,
  canSubmit: false,
  createTeam: /* istanbul ignore next -- @preserve */ async () => undefined,
  createLoading: false,
  reset: noop,
  logoUrl: undefined,
  onSaveImage: undefined,
  requiresOemSegmentSelector: false,
};

export const ManagementTeamModalContext =
  createContext<ManagementTeamModalContextType>(INITIAL_CONTEXT);

export default function ManagementTeamModalProvider({
  children,
}: React.PropsWithChildren<object>) {
  const [values, setValues] =
    useState<ManagementTeamModalValues>(defaultValues);
  const [{ logoUrl }, { uploadImage, onSaveImage }] = useSentinelTeamForm({});
  const [sourceIdError, setSourceIdError] = useState<SourceValidationError[]>(
    [],
  );

  const { notify } = useNotifications();

  const {
    isSuperUser,
    isOemManager,
    oemSegments: rawOemSegments,
  } = useCurrentUserContext();
  /* istanbul ignore next -- @preserve */
  const oemSegments = useMemo(() => rawOemSegments ?? [], [rawOemSegments]);

  const {
    teamName,
    applicationName,
    teamKey,
    sourceIds,
    username,
    password,
    ownerEmail,
    oemSegmentId,
  } = values;

  // Whether the OEM Segment selector needs to be shown:
  // - Super Admin: always show
  // - OEM Manager with multiple segments: show
  // - OEM Manager with one segment: hide (auto-assigned from their single segment)
  const requiresOemSegmentSelector = useMemo(
    () => !!isSuperUser || (!!isOemManager && oemSegments.length > 1),
    [isSuperUser, isOemManager, oemSegments.length],
  );

  // OEM Managers with a single segment have it auto-assigned rather than shown in the selector
  const effectiveOemSegmentId = useMemo(() => {
    if (!requiresOemSegmentSelector && isOemManager && oemSegments.length === 1)
      return oemSegments[0].id;

    return oemSegmentId;
  }, [requiresOemSegmentSelector, isOemManager, oemSegments, oemSegmentId]);

  // API credentials are only required when there is at least one fleet ID
  const apiCredentialsRequired = sourceIds.length > 0;

  // Fleet IDs are optional: valid when all added IDs pass validation, or when none are added
  const sourceIdsValid = useMemo(
    () =>
      sourceIds.length === 0 || sourceIds.every((src) => src.valid === true),
    [sourceIds],
  );

  const invalidEmailSources = useMemo(
    () =>
      sourceIds.filter(
        (src) => src.emailValid === false && src.validated === true,
      ),
    [sourceIds],
  );

  const sourceIdsWithErrors = useMemo(
    () =>
      sourceIds.filter((src) => src.valid === false && src.validated === true),
    [sourceIds],
  );

  // emailValid: no sources present = valid; otherwise at least one source must have a valid email
  const emailValid = useMemo(
    () =>
      sourceIds.length === 0 ||
      sourceIds.some((src) => src.emailValid === true),
    [sourceIds],
  );

  // OEM Segment is required only when the selector is visible
  const oemSegmentValid =
    !requiresOemSegmentSelector || !!effectiveOemSegmentId;
  const requiredFieldsFilled =
    !!teamName &&
    !!teamKey &&
    !!ownerEmail &&
    (!apiCredentialsRequired || (!!username && !!password));
  const canSubmit = useMemo(
    () =>
      requiredFieldsFilled && sourceIdsValid && emailValid && oemSegmentValid,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      teamName,
      teamKey,
      username,
      password,
      ownerEmail,
      sourceIds.length,
      sourceIdsValid,
      emailValid,
      requiresOemSegmentSelector,
      effectiveOemSegmentId,
    ],
  );

  const canGoNext = useCallback(
    /* istanbul ignore next -- @preserve */
    (pageNumber: number) => {
      if (pageNumber === 1) return !!(teamKey && teamName && oemSegmentValid);

      return false;
    },
    [teamKey, teamName, oemSegmentValid],
  );

  /* istanbul ignore next -- @preserve */
  const [createTeamMutation, { loading: createLoading }] =
    useCreateManagementTeamMutation({
      onCompleted: ({ createSentinelTeam }) => {
        if (createSentinelTeam?.sentinelTeam?.id && uploadImage) {
          uploadImage(createSentinelTeam.sentinelTeam.id);
        }

        notify({
          id: 'management-create-team-success',
          duration: 5000,
          onClose: noop,
          intent: 'success',
          title: t`Team ${teamName} successfully created`,
        });
      },
      onError: /* istanbul ignore next -- @preserve */ () => {
        notify({
          id: 'management-create-team-error',
          duration: 5000,
          onClose: noop,
          intent: 'danger',
          title: t`Failed to create team ${teamName}. Try again or contact support if error persists.`,
        });
      },
    });

  const createTeam = useCallback(async () => {
    await createTeamMutation({
      variables: {
        teamKey,
        applicationName,
        name: teamName,
        sources:
          sourceIds.length > 0
            ? sourceIds.map(({ value, sourceType }) => ({
                sourceId: value,
                sourceType,
              }))
            : undefined,
        ownerEmail,
        apiUser: username && password ? { username, password } : null,
        oemSegmentId: effectiveOemSegmentId,
      },
    });
  }, [
    applicationName,
    createTeamMutation,
    effectiveOemSegmentId,
    ownerEmail,
    password,
    sourceIds,
    teamKey,
    teamName,
    username,
  ]);

  const reset = useCallback(() => {
    setValues(defaultValues);
    setSourceIdError([]);
  }, []);

  const contextValue = useMemo(
    () => ({
      values,
      setValues,
      isEditMode: false as const,
      canSubmit,
      createTeam,
      createLoading,
      canGoNext,
      invalidEmailSources,
      sourceIdsWithErrors,
      logoUrl,
      onSaveImage,
      reset,
      sourceIdsValid,
      sourceIdError,
      setSourceIdError,
      requiresOemSegmentSelector,
    }),
    [
      canGoNext,
      canSubmit,
      createLoading,
      createTeam,
      invalidEmailSources,
      logoUrl,
      onSaveImage,
      reset,
      sourceIdsValid,
      sourceIdsWithErrors,
      values,
      sourceIdError,
      requiresOemSegmentSelector,
    ],
  );

  return (
    <ManagementTeamModalContext.Provider value={contextValue}>
      {children}
    </ManagementTeamModalContext.Provider>
  );
}

export function useManagementTeamModalContext() {
  return useContext(ManagementTeamModalContext);
}
